import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { supabase } from '../../../../../shared/lib/supabase'
import { useAuth } from '../../../../../contexts/AuthContext'
import { logAuditEvent } from '../../../../../shared/lib/auditLog'
import { parseMaesterJson } from '../../../../../shared/utils/maesterParser'
import { parseSecureScoreJson } from '../../../../../shared/utils/secureScoreParser'

const SCAN_TYPES = [
  { value: 'maester', label: 'Maester', helper: 'Pester JSON output from Invoke-Maester' },
  { value: 'secure_score', label: 'Secure Score', helper: 'Microsoft Graph /security/secureScores response' }
]

// Phase CT-A: Control Testing tab. Manual JSON upload of Maester results,
// per-scan summary view, framework-tag rollups, and per-test result browser.
//
// Design intent: show advisors what passed, what failed, and which framework
// each test maps to. Future phases (CT-B onward) will extend to GitHub Action
// ingestion, downstream maturity-evidence wiring, and POA&M auto-population.

const MAX_FILE_SIZE = 25 * 1024 * 1024

function formatPercent(num, denom) {
  if (!denom) return '—'
  return `${Math.round((num / denom) * 100)}%`
}

function ResultDot({ result }) {
  const tones = {
    passed: 'bg-green-500',
    failed: 'bg-red-500',
    skipped: 'bg-gray-400',
    not_run: 'bg-gray-300 dark:bg-gray-600',
    inconclusive: 'bg-amber-500'
  }
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${tones[result] || tones.not_run}`} aria-hidden />
}

function ResultLabel({ result }) {
  const tones = {
    passed: 'text-green-700 dark:text-green-400',
    failed: 'text-red-700 dark:text-red-400',
    skipped: 'text-gray-600 dark:text-gray-400',
    not_run: 'text-gray-500 dark:text-gray-500',
    inconclusive: 'text-amber-700 dark:text-amber-400'
  }
  const labels = {
    passed: 'Passed',
    failed: 'Failed',
    skipped: 'Skipped',
    not_run: 'Not run',
    inconclusive: 'Inconclusive'
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium">
      <ResultDot result={result} />
      <span className={tones[result] || tones.not_run}>{labels[result] || result}</span>
    </span>
  )
}

function ControlTestingTab({ client }) {
  const { user } = useAuth()
  const clientId = client?.id

  const [scans, setScans] = useState([])
  const [latestScan, setLatestScan] = useState(null)
  const [latestResults, setLatestResults] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [parseWarnings, setParseWarnings] = useState([])
  const [uploading, setUploading] = useState(false)
  const [notes, setNotes] = useState('')
  const [scanType, setScanType] = useState('maester')
  const [resultFilter, setResultFilter] = useState('all')
  const [frameworkFilter, setFrameworkFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [deleteScanId, setDeleteScanId] = useState(null)
  const fileInputRef = useRef(null)

  const fetchScans = useCallback(async () => {
    if (!clientId) return
    setLoading(true)
    try {
      const { data: scanRows, error: scanErr } = await supabase
        .from('client_control_test_scans')
        .select('*')
        .eq('client_id', clientId)
        .order('scanned_at', { ascending: false })
      if (scanErr) throw scanErr
      setScans(scanRows || [])

      const latest = (scanRows || [])[0] || null
      setLatestScan(latest)

      if (latest) {
        const { data: resultRows, error: resErr } = await supabase
          .from('client_control_test_results')
          .select('*')
          .eq('scan_id', latest.id)
          .order('result', { ascending: true })
          .order('test_name', { ascending: true })
        if (resErr) throw resErr
        setLatestResults(resultRows || [])
      } else {
        setLatestResults([])
      }
    } catch (err) {
      console.error('[ControlTesting] Load failed:', err)
      setError('Unable to load scans: ' + err.message)
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => { fetchScans() }, [fetchScans])

  const handleFile = async (file) => {
    if (!file || !clientId) return
    setError('')
    setParseWarnings([])

    if (file.size > MAX_FILE_SIZE) {
      setError('File size must be under 25MB')
      return
    }
    if (!file.name.toLowerCase().endsWith('.json')) {
      setError('Only .json files are supported')
      return
    }

    setUploading(true)
    try {
      const text = await file.text()
      let parsed
      try {
        parsed = scanType === 'secure_score'
          ? parseSecureScoreJson(text)
          : parseMaesterJson(text)
      } catch (parseErr) {
        setError('Could not parse: ' + parseErr.message)
        return
      }

      if (parsed.results.length === 0 && scanType !== 'secure_score') {
        setError('No test results found in the uploaded file. Verify this is a Maester / Pester JSON output.')
        return
      }

      // Insert scan row
      const { data: scanRow, error: insertErr } = await supabase
        .from('client_control_test_scans')
        .insert({
          client_id: clientId,
          scan_type: scanType,
          source: 'manual_upload',
          tenant_id: parsed.scan.tenant_id,
          tenant_domain: parsed.scan.tenant_domain,
          tool_version: parsed.scan.tool_version,
          scanned_at: parsed.scan.scanned_at,
          uploaded_by: user?.id,
          total_tests: parsed.scan.total_tests,
          passed_count: parsed.scan.passed_count,
          failed_count: parsed.scan.failed_count,
          skipped_count: parsed.scan.skipped_count,
          not_run_count: parsed.scan.not_run_count,
          raw_payload: parsed.scan.raw_payload || {},
          notes: notes.trim() || null
        })
        .select('*')
        .single()
      if (insertErr) throw insertErr

      // Bulk insert results in chunks so very large scans don't blow the
      // PostgREST request size limit.
      const batchSize = 50
      const rows = parsed.results.map(r => ({ ...r, scan_id: scanRow.id, client_id: clientId }))
      for (let i = 0; i < rows.length; i += batchSize) {
        const chunk = rows.slice(i, i + batchSize)
        const { error: bulkErr } = await supabase
          .from('client_control_test_results')
          .insert(chunk)
        if (bulkErr) {
          console.error('[ControlTesting] Bulk insert chunk failed', {
            chunkIndex: i / batchSize,
            chunkSize: chunk.length,
            message: bulkErr.message,
            details: bulkErr.details,
            hint: bulkErr.hint,
            code: bulkErr.code
          })
          throw bulkErr
        }
      }

      logAuditEvent({
        category: 'change',
        action: 'control_test.uploaded',
        description: `Uploaded ${parsed.scan.total_tests} Maester test results (${parsed.scan.passed_count} passed, ${parsed.scan.failed_count} failed)`,
        clientId,
        clientName: client?.name,
        resourceType: 'control_test_scan',
        metadata: {
          scanId: scanRow.id,
          source: 'manual_upload',
          totals: parsed.scan
        }
      })

      setNotes('')
      setParseWarnings(parsed.parseWarnings)
      if (fileInputRef.current) fileInputRef.current.value = ''
      await fetchScans()
    } catch (err) {
      console.error('[ControlTesting] Upload failed:', {
        message: err?.message,
        details: err?.details,
        hint: err?.hint,
        code: err?.code,
        status: err?.status,
        raw: err
      })
      const detail = [err?.message, err?.details, err?.hint].filter(Boolean).join(' — ') || 'unknown error'
      setError('Upload failed: ' + detail)
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async () => {
    if (!deleteScanId) return
    try {
      const { error: delErr } = await supabase
        .from('client_control_test_scans')
        .delete()
        .eq('id', deleteScanId)
      if (delErr) throw delErr
      logAuditEvent({
        category: 'change',
        action: 'control_test.deleted',
        description: `Deleted control test scan`,
        clientId, clientName: client?.name, resourceType: 'control_test_scan',
        metadata: { scanId: deleteScanId }
      })
      setDeleteScanId(null)
      await fetchScans()
    } catch (err) {
      setError('Delete failed: ' + err.message)
    }
  }

  // ─── Derived view: framework rollups + filtered list ───

  const frameworkOptions = useMemo(() => {
    const set = new Set()
    latestResults.forEach(r => (r.framework_tags || []).forEach(t => set.add(t)))
    return Array.from(set).sort()
  }, [latestResults])

  const frameworkRollup = useMemo(() => {
    const map = {}
    for (const r of latestResults) {
      const tags = r.framework_tags?.length ? r.framework_tags : ['Untagged']
      for (const tag of tags) {
        if (!map[tag]) map[tag] = { passed: 0, failed: 0, skipped: 0, not_run: 0, total: 0 }
        map[tag].total++
        if (r.result === 'passed') map[tag].passed++
        else if (r.result === 'failed') map[tag].failed++
        else if (r.result === 'skipped') map[tag].skipped++
        else map[tag].not_run++
      }
    }
    return Object.entries(map).map(([fw, counts]) => ({ framework: fw, ...counts })).sort((a, b) => b.failed - a.failed)
  }, [latestResults])

  const filteredResults = useMemo(() => {
    return latestResults.filter(r => {
      if (resultFilter !== 'all' && r.result !== resultFilter) return false
      if (frameworkFilter !== 'all' && !(r.framework_tags || []).includes(frameworkFilter)) return false
      if (search) {
        const q = search.toLowerCase()
        const hay = `${r.test_name || ''} ${r.test_id || ''} ${r.category || ''} ${(r.tags || []).join(' ')}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
  }, [latestResults, resultFilter, frameworkFilter, search])

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Control Testing</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          Upload Maester scan results to surface factual M365 / Microsoft Entra control evidence. Results inform downstream maturity, threat, and insurance analyses.
        </p>
      </div>

      {/* Upload */}
      <div className="card p-5">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Upload Scan Results</h3>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              {SCAN_TYPES.find(t => t.value === scanType)?.helper || 'External control test output'}. Latest scan of each type drives downstream analyses.
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            {SCAN_TYPES.map(t => (
              <button
                key={t.value}
                type="button"
                onClick={() => setScanType(t.value)}
                disabled={uploading}
                className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                  scanType === t.value
                    ? 'bg-primary-50 dark:bg-primary-900/30 border-primary-300 dark:border-primary-700 text-primary-700 dark:text-primary-300'
                    : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                } disabled:opacity-60`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional scan notes (e.g. 'Q1 2026 baseline run')"
            className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <div className="flex items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={(e) => handleFile(e.target.files?.[0])}
              disabled={uploading}
              className="block w-full text-sm text-gray-700 dark:text-gray-300 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-gray-100 dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-200 hover:file:bg-gray-200 dark:hover:file:bg-gray-600 disabled:opacity-60"
            />
            {uploading && (
              <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap inline-flex items-center gap-1.5">
                <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Uploading...
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">
            JSON only, 25MB max. Latest scan drives all downstream analyses; prior scans are kept in history.
          </p>
        </div>

        {error && (
          <div className="mt-3 text-xs text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg px-3 py-2">
            {error}
          </div>
        )}
        {parseWarnings.length > 0 && (
          <div className="mt-3 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
            <p className="font-semibold mb-1">Parse warnings:</p>
            <ul className="list-disc pl-4 space-y-0.5">
              {parseWarnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* Loading / empty states */}
      {loading ? (
        <div className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">Loading scans...</div>
      ) : !latestScan ? (
        <div className="card p-8 text-center">
          <svg className="w-10 h-10 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
          <p className="text-sm text-gray-500 dark:text-gray-400">No scans uploaded yet</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Run Maester and upload the JSON output to begin</p>
        </div>
      ) : (
        <>
          {/* Latest scan summary */}
          <div className="card p-5">
            <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Latest Scan</h3>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                  {scanTypeLabel(latestScan.scan_type)}
                  {latestScan.tenant_domain && ` • ${latestScan.tenant_domain}`}
                  {latestScan.tool_version && ` • v${latestScan.tool_version}`}
                  {' • '}{new Date(latestScan.scanned_at).toLocaleString()}
                </p>
                {latestScan.notes && <p className="text-[11px] text-gray-500 dark:text-gray-400 italic mt-0.5">{latestScan.notes}</p>}
              </div>
            </div>

            {latestScan.scan_type === 'secure_score' ? (
              <SecureScoreSummary scan={latestScan} />
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <SummaryStat label="Total" value={latestScan.total_tests} tone="text-gray-900 dark:text-white" />
                <SummaryStat
                  label="Passed"
                  value={latestScan.passed_count}
                  tone="text-green-700 dark:text-green-400"
                  sub={formatPercent(latestScan.passed_count, latestScan.total_tests)}
                />
                <SummaryStat
                  label="Failed"
                  value={latestScan.failed_count}
                  tone="text-red-700 dark:text-red-400"
                  sub={formatPercent(latestScan.failed_count, latestScan.total_tests)}
                />
                <SummaryStat label="Skipped" value={latestScan.skipped_count} tone="text-gray-600 dark:text-gray-400" />
                <SummaryStat label="Not run" value={latestScan.not_run_count} tone="text-gray-500 dark:text-gray-500" />
              </div>
            )}
          </div>

          {/* Framework rollup */}
          {frameworkRollup.length > 0 && (
            <div className="card p-5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                Framework Coverage
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {frameworkRollup.map(fw => (
                  <div key={fw.framework} className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white truncate">{fw.framework}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums">{fw.passed}/{fw.total}</span>
                    </div>
                    <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500"
                        style={{ width: `${(fw.passed / fw.total) * 100}%` }}
                      />
                    </div>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-gray-600 dark:text-gray-400">
                      {fw.passed > 0 && <span><ResultDot result="passed" /> {fw.passed} passed</span>}
                      {fw.failed > 0 && <span><ResultDot result="failed" /> {fw.failed} failed</span>}
                      {(fw.skipped + fw.not_run) > 0 && <span><ResultDot result="skipped" /> {fw.skipped + fw.not_run} other</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Test result browser */}
          <div className="card p-5">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Test Results</h3>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search tests..."
                  className="px-2.5 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                <select
                  value={resultFilter}
                  onChange={(e) => setResultFilter(e.target.value)}
                  className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">All results</option>
                  <option value="failed">Failed only</option>
                  <option value="passed">Passed only</option>
                  <option value="skipped">Skipped</option>
                  <option value="not_run">Not run</option>
                </select>
                <select
                  value={frameworkFilter}
                  onChange={(e) => setFrameworkFilter(e.target.value)}
                  className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="all">All frameworks</option>
                  {frameworkOptions.map(fw => (
                    <option key={fw} value={fw}>{fw}</option>
                  ))}
                </select>
              </div>
            </div>

            {filteredResults.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 italic py-4 text-center">No tests match the current filters.</p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {filteredResults.map(r => <TestRow key={r.id} result={r} />)}
              </ul>
            )}
          </div>

          {/* Scan history */}
          {scans.length > 1 && (
            <div className="card p-5">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">Scan History</h3>
              <ul className="divide-y divide-gray-100 dark:divide-gray-700/50">
                {scans.slice(1).map(s => (
                  <li key={s.id} className="flex items-center justify-between py-2.5 gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                          {scanTypeLabel(s.scan_type)}
                        </span>
                        <p className="text-sm text-gray-900 dark:text-white">{new Date(s.scanned_at).toLocaleString()}</p>
                      </div>
                      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                        {s.scan_type === 'secure_score' && s.raw_payload?.currentScore !== undefined
                          ? <>Score {Math.round(s.raw_payload.currentScore)}/{Math.round(s.raw_payload.maxScore || 0)} • {s.total_tests} controls</>
                          : <>{s.total_tests} tests • <span className="text-green-700 dark:text-green-400">{s.passed_count} passed</span> • <span className="text-red-700 dark:text-red-400">{s.failed_count} failed</span></>
                        }
                        {s.notes && ` • ${s.notes}`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDeleteScanId(s.id)}
                      className="p-1.5 rounded-md text-gray-500 hover:text-red-500 dark:text-gray-400 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                      title="Delete scan"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {/* Delete confirmation */}
      {deleteScanId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeleteScanId(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-sm mx-4 p-6">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">Delete Scan</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">This removes the scan and all its test results. Cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteScanId(null)} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function scanTypeLabel(type) {
  const map = SCAN_TYPES.reduce((acc, t) => { acc[t.value] = t.label; return acc }, {})
  return map[type] || type
}

function SecureScoreSummary({ scan }) {
  const summary = scan.raw_payload || {}
  const current = Number(summary.currentScore) || 0
  const max = Number(summary.maxScore) || 0
  const pct = summary.percentage ?? (max ? Math.round((current / max) * 100) : null)
  const byCategory = summary.byCategory || {}
  const categories = Object.entries(byCategory).sort((a, b) => b[1].max - a[1].max)
  const services = Array.isArray(summary.enabledServices) ? summary.enabledServices : []

  // Comparative scores from Microsoft (industry / org-size averages)
  const compare = Array.isArray(summary.averageComparativeScores) ? summary.averageComparativeScores : []

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-3 col-span-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Secure Score</p>
          <p className="text-3xl font-bold tabular-nums text-gray-900 dark:text-white mt-0.5">
            {Math.round(current)} <span className="text-base font-normal text-gray-500 dark:text-gray-400">/ {Math.round(max)}</span>
          </p>
          {pct !== null && (
            <div className="mt-2">
              <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full ${pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}
                  style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                />
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1 tabular-nums">{pct}% of max</p>
            </div>
          )}
        </div>
        <SummaryStat label="Controls" value={scan.total_tests} tone="text-gray-900 dark:text-white" />
        <SummaryStat
          label="Fully implemented"
          value={scan.passed_count}
          tone="text-green-700 dark:text-green-400"
          sub={formatPercent(scan.passed_count, scan.total_tests)}
        />
      </div>

      {compare.length > 0 && (
        <div className="text-[11px] text-gray-500 dark:text-gray-400 flex flex-wrap gap-x-4 gap-y-1">
          {compare.map((c, i) => (
            <span key={i}>
              <span className="font-semibold">{c.basis}:</span>{' '}
              <span className="tabular-nums">{Math.round(c.averageScore || 0)}</span>
            </span>
          ))}
        </div>
      )}

      {categories.length > 0 && (
        <div>
          <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">By Category</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {categories.map(([cat, c]) => {
              const catPct = c.max > 0 ? Math.round((c.score / c.max) * 100) : 0
              return (
                <div key={cat} className="rounded-md border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-semibold text-gray-900 dark:text-white truncate">{cat}</span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400 tabular-nums">
                      {Math.round(c.score)}/{Math.round(c.max)}
                    </span>
                  </div>
                  <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${catPct >= 70 ? 'bg-green-500' : catPct >= 40 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${catPct}%` }}
                    />
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-gray-500 dark:text-gray-400">
                    <span>{c.controls} {c.controls === 1 ? 'control' : 'controls'}</span>
                    {c.passed > 0 && <span className="text-green-700 dark:text-green-400">{c.passed} done</span>}
                    {c.partial > 0 && <span className="text-amber-700 dark:text-amber-400">{c.partial} partial</span>}
                    {c.failed > 0 && <span className="text-red-700 dark:text-red-400">{c.failed} todo</span>}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {services.length > 0 && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          <span className="font-semibold">Services:</span> {services.join(', ')}
        </p>
      )}
    </div>
  )
}

function SummaryStat({ label, value, tone, sub }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`text-2xl font-bold tabular-nums ${tone}`}>{value ?? 0}</p>
      {sub && <p className="text-[11px] text-gray-500 dark:text-gray-400">{sub}</p>}
    </div>
  )
}

function TestRow({ result }) {
  const [open, setOpen] = useState(false)
  return (
    <li className="py-2.5">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full text-left flex items-start justify-between gap-3 group"
      >
        <div className="min-w-0 flex-grow">
          <div className="flex items-center gap-2 flex-wrap">
            <ResultLabel result={result.result} />
            <span className="text-sm text-gray-900 dark:text-white">{result.test_name}</span>
            {result.test_id && <span className="text-[10px] text-gray-400 dark:text-gray-500 tabular-nums">{result.test_id}</span>}
          </div>
          {(result.framework_tags?.length > 0 || result.category) && (
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {result.category && <span className="text-[11px] text-gray-500 dark:text-gray-400">{result.category}</span>}
              {result.framework_tags?.length > 0 && (
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  {result.framework_tags.join(' · ')}
                </span>
              )}
            </div>
          )}
        </div>
        <svg className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 mt-1 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="mt-2 ml-6 pl-3 border-l-2 border-gray-100 dark:border-gray-700 space-y-2 text-xs">
          {result.description && (
            <p className="text-gray-600 dark:text-gray-400">{result.description}</p>
          )}
          {result.result_message && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-0.5">Output</p>
              <pre className="text-[11px] text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800 rounded p-2 whitespace-pre-wrap break-words leading-relaxed">{result.result_message}</pre>
            </div>
          )}
          {result.remediation && (
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-0.5">Remediation</p>
              <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{result.remediation}</p>
            </div>
          )}
          {result.reference_url && (
            <a href={result.reference_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary-600 dark:text-primary-400 hover:underline">
              Reference
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
          {result.tags?.length > 0 && (
            <p className="text-[10px] text-gray-500 dark:text-gray-400">
              Tags: {result.tags.join(', ')}
            </p>
          )}
        </div>
      )}
    </li>
  )
}

export default ControlTestingTab
